import { resendInvitation } from "@/api/invitations";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

interface IOptions {
    /**
     * Called in addition to the default `["invitation", invitationId]`
     * invalidation — e.g. the admin users table also invalidates
     * `["admin-users"]` so the row's "last sent" timestamp refreshes.
     */
    onSuccess?: () => void;
}

/**
 * Shared resend-invitation mutation, used by both the user details page
 * (`UserRegistrationStatus` fetching by id) and the admin users table
 * (`UserRegistrationStatus` given prefetched row data).
 */
export const useResendInvitation = (
    invitationId: number,
    { onSuccess }: IOptions = {}
) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () => resendInvitation(invitationId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["invitation", invitationId],
            });
            onSuccess?.();

            enqueueSnackbar("Invitation resent", {
                variant: "success",
            });
        },
        onError: () => {
            enqueueSnackbar("Failed to resend invitation", {
                variant: "error",
            });
        },
    });
};
